import { getConfig } from '@/config';
import { formatSearchConfig } from '@/search-for-retail/common/search/search.formatters';
import { searchApi } from './searchApi';
// ---------------------------------------------------------------------------
// Shared query builders
// ---------------------------------------------------------------------------
function buildFacetQuery({ clientKey, facetSearchConfig }) {
    return {
        url: `${getConfig().searchApiUrl}/facet`,
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `client-key ${clientKey}`,
        },
        body: facetSearchConfig,
    };
}
// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------
/**
 * RTK Query endpoints for common (shared) search operations.
 *
 * All endpoints POST to `searchApi`'s base URL (`SEARCH_API_URL`) or its
 * `/facet` sub-path. Auth headers are injected per-request because `clientKey`
 * is dynamic (per-area/per-collection) and not available at API creation time.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const commonSearchApi = searchApi.injectEndpoints({
    endpoints: (builder) => ({
        // -----------------------------------------------------------------------
        // Common search queries
        // -----------------------------------------------------------------------
        /**
         * Executes a search request against the base Search API URL.
         *
         * @endpoint POST /
         * @returns SearchDTO
         */
        getSearchResults: builder.query({
            query: ({ clientKey, searchConfig }) => ({
                url: getConfig().searchApiUrl,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `client-key ${clientKey}`,
                },
                body: formatSearchConfig(searchConfig),
            }),
        }),
        /**
         * Fetches available navigations for a given facet search config.
         *
         * @endpoint POST /facet
         * @returns AvailableNavigation (single) extracted from response
         */
        getSearchAvailableNavigations: builder.query({
            query: buildFacetQuery,
            transformResponse: (response) => response === null || response === void 0 ? void 0 : response.availableNavigation,
        }),
        /**
         * Fetches facet refinement values for autocomplete inputs.
         *
         * @endpoint POST /facet
         * @returns string[] of refinement values extracted from response
         */
        getSearchFacets: builder.query({
            query: buildFacetQuery,
            transformResponse: (response) => {
                const availableNav = response === null || response === void 0 ? void 0 : response.availableNavigation;
                if (!(availableNav === null || availableNav === void 0 ? void 0 : availableNav.refinements) || !Array.isArray(availableNav.refinements)) {
                    return [];
                }
                return availableNav.refinements
                    .map((r) => r === null || r === void 0 ? void 0 : r.value)
                    .filter((value) => Boolean(value));
            },
        }),
        /**
         * Executes a preview search request (supports browse preview type).
         *
         * @endpoint POST /
         * @returns SearchDTO
         */
        getSearchPreviewResults: builder.query({
            query: ({ clientKey, searchConfig, previewType }) => ({
                url: getConfig().searchApiUrl,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `client-key ${clientKey}`,
                },
                body: formatSearchConfig(searchConfig, previewType),
            }),
        }),
    }),
});
export const { useLazyGetSearchResultsQuery, useLazyGetSearchAvailableNavigationsQuery, useLazyGetSearchFacetsQuery, useLazyGetSearchPreviewResultsQuery, } = commonSearchApi;
